import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { UsersRound } from "lucide-react";

import SynopsisSectionCard from "./SynopsisSectionCard";
import type {
  AccountTeamMember,
  CustomerTeamMember,
  ExecutiveTeamMember,
} from "../../../Store/Reducers/AccountDetailsSlice";
import { getInitials } from "../../../Utils/Helper/Helper";
import {
  ACCOUNT_TEAM_AVATAR_BG,
  CUSTOMER_CONTACT_AVATAR_BG,
  EXEC_SPONSOR_AVATAR_BG,
} from "../../../Utils/Helper/profileAvatarUi";
import {
  getAccountTeamRoleCode,
  meddpiccCanonicalToMatrixKey,
  readCustomerMeddpiccRole,
} from "../../Relationship/account-relationships/relationshipUtils";

type TeamsOverviewSectionProps = {
  customerMembers: CustomerTeamMember[];
  customerLoading: boolean;
  accountTeamMembers: AccountTeamMember[];
  executiveSponsors: ExecutiveTeamMember[];
  vendorLoading: boolean;
  onOpenTeams: () => void;
};

type VendorPreview = {
  id: string;
  name: string;
  subtitle: string;
  avatarBg: string;
  sponsorBadge?: "primary" | "secondary" | null;
};

/** Horizontal row — thin scrollbar always reserved; thumb only visible on hover (no layout jump). */
const TEAM_ROW_SCROLL_CLASS =
  "group/team-scroll flex gap-2.5 overflow-x-auto pb-0.5 [scrollbar-width:thin] [scrollbar-color:transparent_transparent] hover:[scrollbar-color:#d1d5db_transparent] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-transparent hover:[&::-webkit-scrollbar-thumb]:bg-gray-300";

const TeamRowScroller: React.FC<{
  children: React.ReactNode;
  itemCount: number;
}> = ({ children, itemCount }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(maxScroll > 2 && el.scrollLeft < maxScroll - 2);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollState();

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(el);

    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);

    return () => {
      resizeObserver.disconnect();
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState, itemCount]);

  return (
    <div className="relative">
      {canScrollLeft ? (
        <div
          className="pointer-events-none absolute inset-y-0 left-0 z-10 w-8 bg-gradient-to-r from-white to-transparent"
          aria-hidden
        />
      ) : null}
      {canScrollRight ? (
        <div
          className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-white via-white/90 to-transparent"
          aria-hidden
        />
      ) : null}
      <div ref={scrollRef} className={TEAM_ROW_SCROLL_CLASS}>
        {children}
      </div>
    </div>
  );
};

const formatRoleLabel = (value: string | null | undefined): string => {
  if (!value) return "Contact";
  const trimmed = value.trim();
  if (!trimmed) return "Contact";
  if (trimmed.includes(" ") || trimmed.includes("(")) return trimmed;
  return trimmed
    .split("_")
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const resolveExecSponsorBadge = (
  member: ExecutiveTeamMember
): "primary" | "secondary" | null => {
  const type = member.executive_type?.trim()?.toLowerCase();
  if (type === "primary") return "primary";
  if (type === "secondary") return "secondary";
  return null;
};

const buildVendorPreviews = (
  executiveSponsors: ExecutiveTeamMember[],
  accountTeamMembers: AccountTeamMember[]
): VendorPreview[] => {
  const sponsors = executiveSponsors.map(member => ({
    id: member.user_id,
    name: member.full_name || member.email,
    subtitle: "Executive Sponsor",
    avatarBg: EXEC_SPONSOR_AVATAR_BG,
    sponsorBadge: resolveExecSponsorBadge(member),
  }));

  const accountTeam = accountTeamMembers.map(member => {
    const roleCode = getAccountTeamRoleCode({
      role: member.role_name,
      raw: { role: member.role_name },
    });
    return {
      id: member.user_id,
      name: member.full_name || member.email,
      subtitle: roleCode ? roleCode.toUpperCase() : "Account Team",
      avatarBg: ACCOUNT_TEAM_AVATAR_BG,
      sponsorBadge: null,
    };
  });

  return [...sponsors, ...accountTeam];
};

const SponsorBadge: React.FC<{ type: "primary" | "secondary" }> = ({
  type,
}) => (
  <span
    className={
      type === "primary"
        ? "inline-flex shrink-0 items-center rounded-[3px] border border-amber-300 bg-amber-50 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-amber-800"
        : "inline-flex shrink-0 items-center rounded-[3px] border border-indigo-300 bg-indigo-50 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-indigo-800"
    }
    title={type === "primary" ? "Primary sponsor" : "Secondary sponsor"}
  >
    {type === "primary" ? "P" : "S"}
  </span>
);

const TeamMemberCard: React.FC<{
  name: string;
  subtitle: string;
  avatarBg: string;
  badge?: string | null;
  sponsorBadge?: "primary" | "secondary" | null;
}> = ({ name, subtitle, avatarBg, badge, sponsorBadge }) => (
  <div className="flex w-max max-w-[18rem] shrink-0 items-start gap-2.5 rounded-lg border border-gray-200 bg-white px-3 py-3">
    <div
      className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white"
      style={{ background: avatarBg }}
    >
      {getInitials(name)}
    </div>
    <div className="min-w-0">
      <div className="flex items-center gap-1.5">
        <div className="whitespace-nowrap text-sm font-semibold leading-snug text-gray-900">
          {name}
        </div>
        {sponsorBadge ? <SponsorBadge type={sponsorBadge} /> : null}
        {badge ? (
          <span className="inline-flex shrink-0 rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
            {badge}
          </span>
        ) : null}
      </div>
      <div className="mt-0.5 whitespace-nowrap text-xs leading-snug text-gray-500">
        {subtitle}
      </div>
    </div>
  </div>
);

const TeamColumn: React.FC<{
  title: string;
  loading: boolean;
  emptyMessage: string;
  children: React.ReactNode;
}> = ({ title, loading, emptyMessage, children }) => (
  <div className="min-w-0 flex-1">
    <div className="mb-3">
      <h3 className="m-0 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        {title}
      </h3>
    </div>
    {loading ? <p className="m-0 text-sm text-gray-400">Loading…</p> : children}
    {!loading && !children ? (
      <p className="m-0 text-sm text-gray-400">{emptyMessage}</p>
    ) : null}
  </div>
);

const TeamsOverviewSection: React.FC<TeamsOverviewSectionProps> = ({
  customerMembers,
  customerLoading,
  accountTeamMembers,
  executiveSponsors,
  vendorLoading,
  onOpenTeams,
}) => {
  const visibleCustomers = useMemo(() => customerMembers, [customerMembers]);

  const visibleVendors = useMemo(
    () => buildVendorPreviews(executiveSponsors, accountTeamMembers),
    [accountTeamMembers, executiveSponsors]
  );

  return (
    <SynopsisSectionCard
      icon={UsersRound}
      title="Stakeholders"
      linkLabel="See All"
      onLinkClick={onOpenTeams}
    >
      <div className="flex flex-col gap-5 lg:flex-row lg:gap-0">
        <div className="min-w-0 flex-1 lg:pr-5">
          <TeamColumn
            title="Exec & Account Team"
            loading={vendorLoading}
            emptyMessage="No vendor team members yet."
          >
            {visibleVendors.length > 0 ? (
              <TeamRowScroller itemCount={visibleVendors.length}>
                {visibleVendors.map(member => (
                  <TeamMemberCard
                    key={member.id}
                    name={member.name}
                    subtitle={member.subtitle}
                    avatarBg={member.avatarBg}
                    sponsorBadge={member.sponsorBadge}
                  />
                ))}
              </TeamRowScroller>
            ) : null}
          </TeamColumn>
        </div>

        <div className="hidden w-px shrink-0 self-stretch bg-gray-300 lg:block" />

        <div className="min-w-0 flex-1 border-t border-gray-200 pt-5 lg:border-t-0 lg:pt-0 lg:pl-5">
          <TeamColumn
            title="Account Contacts"
            loading={customerLoading}
            emptyMessage="No customer contacts yet."
          >
            {visibleCustomers.length > 0 ? (
              <TeamRowScroller itemCount={visibleCustomers.length}>
                {visibleCustomers.map(member => {
                  const badgeKey = meddpiccCanonicalToMatrixKey(
                    readCustomerMeddpiccRole(member)
                  );
                  const badge =
                    badgeKey || member.influence_level || member.customer_type;
                  const subtitle =
                    member.position ||
                    formatRoleLabel(member.role) ||
                    "Customer Contact";

                  return (
                    <TeamMemberCard
                      key={member.user_id}
                      name={member.name}
                      subtitle={subtitle}
                      avatarBg={CUSTOMER_CONTACT_AVATAR_BG}
                      badge={badge ? String(badge).toUpperCase() : null}
                    />
                  );
                })}
              </TeamRowScroller>
            ) : null}
          </TeamColumn>
        </div>
      </div>
    </SynopsisSectionCard>
  );
};

export default TeamsOverviewSection;
